import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../../store/authStore';
import { getAcademicYearsDirect, duplicateAcademicYearDirect } from '@timetable/api';
import type { DuplicateAcademicYearRequest } from '@timetable/types';
import { queryKeys } from '../../lib/queryKeys';

interface YearDuplicationDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface FormData {
  sourceYear: string;
  targetYear: string;
  targetStartDate: string;
}

interface DuplicationResult {
  created: number;
  skipped: number;
  errors: string[];
}

export const YearDuplicationDialog: React.FC<YearDuplicationDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const schoolContext = useAuthStore((state) => state.schoolContext);
  const userId = useAuthStore((state) => state.user?.id);

  const [formData, setFormData] = useState<FormData>({
    sourceYear: '',
    targetYear: '',
    targetStartDate: '',
  });
  const [formErrors, setFormErrors] = useState<Partial<FormData>>({});
  const [showPreview, setShowPreview] = useState(false);
  const [duplicationResult, setDuplicationResult] = useState<DuplicationResult | null>(null);

  // Query academic years
  const { data: academicYears, isLoading: yearsLoading } = useQuery({
    queryKey: queryKeys.academicYears.list({ school_id: schoolContext }),
    queryFn: () => getAcademicYearsDirect({ school_id: schoolContext! }),
    enabled: !!schoolContext && open,
  });

  // Duplication mutation
  const duplicationMutation = useMutation({
    mutationFn: (request: DuplicateAcademicYearRequest) => duplicateAcademicYearDirect(request),
    onSuccess: (result) => {
      setDuplicationResult(result);
      if (onSuccess) {
        onSuccess();
      }
    },
  });

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setFormData({
        sourceYear: '',
        targetYear: '',
        targetStartDate: '',
      });
      setFormErrors({});
      setShowPreview(false);
      setDuplicationResult(null);
    }
  }, [open]);

  const sourceYearData = academicYears?.find((year) => year.name === formData.sourceYear);
  const targetYearData = academicYears?.find((year) => year.name === formData.targetYear);

  const validateForm = (): boolean => {
    const errors: Partial<FormData> = {};

    if (!formData.sourceYear) {
      errors.sourceYear = 'Source year is required';
    }

    if (!formData.targetYear) {
      errors.targetYear = 'Target year is required';
    }

    if (formData.sourceYear && formData.targetYear && formData.sourceYear === formData.targetYear) {
      errors.targetYear = 'Target year must be different from source year';
    }

    if (!formData.targetStartDate) {
      errors.targetStartDate = 'Target start date is required';
    }

    if (formData.targetStartDate) {
      const targetDate = new Date(formData.targetStartDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (targetDate < today) {
        errors.targetStartDate = 'Target start date cannot be in the past';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePreview = () => {
    if (validateForm()) {
      setShowPreview(true);
    }
  };

  const handleDuplicate = () => {
    if (!validateForm() || !userId) return;

    const request: DuplicateAcademicYearRequest = {
      source_year: formData.sourceYear,
      target_year: formData.targetYear,
      target_start_date: formData.targetStartDate,
      created_by: userId,
    };

    duplicationMutation.mutate(request);
  };

  const handleClose = () => {
    if (!duplicationMutation.isPending) {
      onClose();
    }
  };

  const handleBack = () => {
    setShowPreview(false);
  };

  const renderFormStep = () => (
    <>
      <DialogContent>
        <Stack spacing={3}>
          <Alert severity="info">
            This will copy all active timetable records from the source year to the target year.
            Only records that are currently active (not superseded) will be copied.
          </Alert>

          <FormControl fullWidth error={!!formErrors.sourceYear} required>
            <InputLabel>Source Academic Year</InputLabel>
            <Select
              value={formData.sourceYear}
              onChange={(e) => setFormData({ ...formData, sourceYear: e.target.value })}
              label="Source Academic Year"
              disabled={yearsLoading}
            >
              {academicYears?.map((year) => (
                <MenuItem key={year.id} value={year.name}>
                  {year.name} ({new Date(year.start_date).toLocaleDateString()} -{' '}
                  {new Date(year.end_date).toLocaleDateString()})
                </MenuItem>
              ))}
            </Select>
            {formErrors.sourceYear && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {formErrors.sourceYear}
              </Typography>
            )}
          </FormControl>

          <FormControl fullWidth error={!!formErrors.targetYear} required>
            <InputLabel>Target Academic Year</InputLabel>
            <Select
              value={formData.targetYear}
              onChange={(e) => setFormData({ ...formData, targetYear: e.target.value })}
              label="Target Academic Year"
              disabled={yearsLoading}
            >
              {academicYears
                ?.filter((year) => year.name !== formData.sourceYear)
                .map((year) => (
                  <MenuItem key={year.id} value={year.name}>
                    {year.name} ({new Date(year.start_date).toLocaleDateString()} -{' '}
                    {new Date(year.end_date).toLocaleDateString()})
                  </MenuItem>
                ))}
            </Select>
            {formErrors.targetYear && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {formErrors.targetYear}
              </Typography>
            )}
          </FormControl>

          <TextField
            label="Target Start Date"
            type="date"
            value={formData.targetStartDate}
            onChange={(e) => setFormData({ ...formData, targetStartDate: e.target.value })}
            error={!!formErrors.targetStartDate}
            helperText={
              formErrors.targetStartDate ||
              'The date when the duplicated timetable becomes active'
            }
            InputLabelProps={{ shrink: true }}
            fullWidth
            required
          />

          {sourceYearData && (
            <Alert severity="info" icon={<WarningIcon />}>
              <Typography variant="body2">
                <strong>Note:</strong> Only active records (not superseded) from{' '}
                <strong>{sourceYearData.name}</strong> will be copied. If any referenced teachers,
                subjects, or class sections no longer exist, those records will be skipped.
              </Typography>
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={duplicationMutation.isPending}>
          Cancel
        </Button>
        <Button
          onClick={handlePreview}
          variant="contained"
          disabled={duplicationMutation.isPending || yearsLoading}
        >
          Preview
        </Button>
      </DialogActions>
    </>
  );

  const renderPreviewStep = () => (
    <>
      <DialogContent>
        <Stack spacing={3}>
          <Alert severity="warning">
            <Typography variant="body2">
              <strong>Review the duplication details before proceeding.</strong> This operation
              cannot be undone.
            </Typography>
          </Alert>

          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Duplication Summary
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="Source Year"
                  secondary={
                    sourceYearData
                      ? `${sourceYearData.name} (${new Date(
                          sourceYearData.start_date
                        ).toLocaleDateString()} - ${new Date(
                          sourceYearData.end_date
                        ).toLocaleDateString()})`
                      : formData.sourceYear
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Target Year"
                  secondary={
                    targetYearData
                      ? `${targetYearData.name} (${new Date(
                          targetYearData.start_date
                        ).toLocaleDateString()} - ${new Date(
                          targetYearData.end_date
                        ).toLocaleDateString()})`
                      : formData.targetYear
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Effective Date"
                  secondary={new Date(formData.targetStartDate).toLocaleDateString()}
                />
              </ListItem>
            </List>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              What will be copied:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="Active timetable records"
                  secondary="Only records where valid_to is NULL (currently active)"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Day-of-week assignments"
                  secondary="Monday through Friday period assignments"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Teacher-subject-class mappings"
                  secondary="All period assignments with their relationships"
                />
              </ListItem>
            </List>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              What will NOT be copied:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="Historical records"
                  secondary="Records that have been superseded (valid_to is not NULL)"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Temporary assignments"
                  secondary="Substitute teacher assignments from the assignments table"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Records with missing references"
                  secondary="Records where teachers, subjects, or classes no longer exist"
                />
              </ListItem>
            </List>
          </Box>

          {duplicationMutation.isError && (
            <Alert severity="error">
              <Typography variant="body2">
                <strong>Error:</strong>{' '}
                {duplicationMutation.error instanceof Error
                  ? duplicationMutation.error.message
                  : 'Failed to duplicate academic year'}
              </Typography>
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleBack} disabled={duplicationMutation.isPending}>
          Back
        </Button>
        <Button
          onClick={handleDuplicate}
          variant="contained"
          color="primary"
          disabled={duplicationMutation.isPending}
          startIcon={duplicationMutation.isPending ? undefined : <CopyIcon />}
        >
          {duplicationMutation.isPending ? 'Duplicating...' : 'Duplicate'}
        </Button>
      </DialogActions>
      {duplicationMutation.isPending && <LinearProgress />}
    </>
  );

  const renderResultStep = () => (
    <>
      <DialogContent>
        <Stack spacing={3}>
          {duplicationResult && duplicationResult.created > 0 && (
            <Alert severity="success" icon={<CheckCircleIcon />}>
              <Typography variant="body2">
                <strong>Duplication completed successfully!</strong>
              </Typography>
            </Alert>
          )}

          {duplicationResult && duplicationResult.created === 0 && duplicationResult.skipped > 0 && (
            <Alert severity="warning" icon={<WarningIcon />}>
              <Typography variant="body2">
                <strong>No records were created.</strong> All records were skipped.
              </Typography>
            </Alert>
          )}

          {duplicationResult && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Summary
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary={`${duplicationResult.created} records created`}
                    secondary="Successfully copied to the target year"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={`${duplicationResult.skipped} records skipped`}
                    secondary="Records that could not be copied due to missing references"
                  />
                </ListItem>
              </List>
            </Box>
          )}

          {duplicationResult && duplicationResult.errors.length > 0 && (
            <Box>
              <Alert severity="warning" icon={<ErrorIcon />}>
                <Typography variant="body2" gutterBottom>
                  <strong>Some records were skipped:</strong>
                </Typography>
              </Alert>
              <Box
                sx={{
                  mt: 2,
                  maxHeight: 200,
                  overflowY: 'auto',
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                }}
              >
                <List dense>
                  {duplicationResult.errors.map((error, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={error}
                        primaryTypographyProps={{
                          variant: 'body2',
                          color: 'text.secondary',
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Box>
          )}

          <Alert severity="info">
            <Typography variant="body2">
              The duplicated timetable is now available in <strong>{formData.targetYear}</strong>{' '}
              starting from{' '}
              <strong>{new Date(formData.targetStartDate).toLocaleDateString()}</strong>. You can
              now make adjustments as needed.
            </Typography>
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {duplicationResult ? 'Duplication Complete' : 'Duplicate Academic Year'}
      </DialogTitle>
      {duplicationResult
        ? renderResultStep()
        : showPreview
        ? renderPreviewStep()
        : renderFormStep()}
    </Dialog>
  );
};
